import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AIFilterConfig } from '@new-signal/shared';

interface AIFilterCardProps {
  config: AIFilterConfig;
  onChange: (config: AIFilterConfig) => void;
  onRemove: () => void;
  error?: string | null;
}

export function AIFilterCard({ config, onChange, onRemove, error }: AIFilterCardProps) {
  const { t } = useTranslation('feed-builder');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  return (
    <div className="mb-4 rounded-xl border border-brand-border bg-brand-canvas p-4">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">&#10024;</span>
          <strong>{t('aiFilter.title')}</strong>
        </div>
        <button
          onClick={onRemove}
          title={t('aiFilter.removeAction')}
          className="cursor-pointer border-none bg-transparent px-1 text-lg leading-none text-brand-muted"
        >
          &times;
        </button>
      </div>

      {/* Description textarea */}
      <label className="mb-1 block text-sm text-brand-subtle">{t('aiFilter.descriptionLabel')}</label>
      <textarea
        value={config.description}
        onChange={(e) => onChange({ ...config, description: e.target.value })}
        placeholder={t('aiFilter.placeholder')}
        rows={3}
        className="mb-2 w-full resize-y rounded border border-brand-border-medium p-2 text-sm"
      />

      {error && (
        <p className="mb-2 text-[13px] text-brand-error">{error}</p>
      )}

      {/* Advanced section */}
      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="mb-2 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-brand-subtle hover:text-brand-text"
      >
        {advancedOpen ? '▲' : '▼'} {t('aiFilter.advanced')}
      </button>

      {advancedOpen && (
        <div className="mb-2 rounded border border-brand-border bg-white p-3">
          <label className="mb-1 block text-sm text-brand-subtle">
            {t('aiFilter.thresholdLabel')}: {config.threshold.toFixed(1)}
          </label>
          <input
            type="range"
            min="0.5"
            max="0.9"
            step="0.1"
            value={config.threshold}
            onChange={(e) => onChange({ ...config, threshold: parseFloat(e.target.value) })}
            className="w-full"
          />
          <div className="flex justify-between text-[11px] text-brand-muted">
            <span>0.5</span>
            <span>0.9</span>
          </div>
        </div>
      )}

      {/* Cost notice */}
      <p className="mt-1 text-[12px] text-brand-muted">
        {t('aiFilter.costNotice')}
      </p>
    </div>
  );
}
